import { Injectable } from '@angular/core';
import { InsightsRestService } from '../../service/insights-rest-service';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { StoreRankingRequest } from './store-ranking-request';
import { Observable, throwError } from 'rxjs';
import { StoreRanking } from './store-ranking';
import { catchError, map } from 'rxjs/operators';

/**
 * Service for Store Ranking API operations
 *
 * API Endpoint: POST /api/insights/store/<STORE_UUID>/ranking
 *
 * Task in progress – verify payload mapping with existing filters
 *
 * cURL Example (for reference):
 * ```
 * curl -X POST 'https://<HOST>/api/insights/store/<STORE_UUID>/ranking' \
 *   -H 'Authorization: Bearer <ACCESS_TOKEN>' \
 *   -H 'Content-Type: application/json' \
 *   -d '{
 *     "geoJson": { ... },
 *     "comparisonType": "BANNER",
 *     "keyIndicator": "SALES_VOLUME",
 *     "salesSqftDisplayType": "TOTAL_AREA",
 *     "salesVolumeDisplayType": "WEEKLY",
 *     "bannerUuid": "<BANNER_UUID>"
 *   }'
 * ```
 *
 * Response: Array of StoreRanking objects
 * {
 *   "percentile": 26,
 *   "rank": 15,
 *   "totalCount": 20,
 *   "value": -8.571428571428571,
 *   "store": { ... }
 * }
 *
 * Note: Authorization header is automatically added by AuthorizationHeaderInterceptor
 */
@Injectable({
  providedIn: 'root'
})
export class StoreRankingService extends InsightsRestService {

  constructor(private http: HttpClient) {
    super();
  }

  /**
   * Fetches store rankings based on the provided filters
   *
   * @param storeUuid - The UUID of the store to get rankings for (replaces <STORE_UUID> in URL)
   * @param request - StoreRankingRequest containing filter criteria aligned with UI state
   * @returns Observable<StoreRanking[]> - Array of store rankings sorted by the selected key indicator
   *
   * Reuse existing filter logic where applicable:
   * - geoJson: Derived from marketArea (FeatureCollection from DetailState)
   * - comparisonType: Maps from UI store type filter (All Grocery Stores, Same Banner, Same Fit Category)
   * - keyIndicator: User preference for ranking metric (SALES_VOLUME, SALES_SQFT, STORE_COUNT)
   * - salesSqftDisplayType: User preference (TOTAL_AREA, SALES_AREA)
   * - salesVolumeDisplayType: User preference (ANNUAL, WEEKLY)
   * - bannerUuid: Required when comparisonType is BANNER
   * - fitCategory: Required when comparisonType is FIT_CATEGORY
   */
  findAllRankings(storeUuid: string, request: StoreRankingRequest): Observable<StoreRanking[]> {
    // Build URL: /api/insights/store/<STORE_UUID>/ranking
    const url = this.buildUrl(`/store/${storeUuid}/ranking`);

    // Task in progress – verify payload mapping with existing filters
    // The request body is dynamically constructed from UI filter state
    return this.http.post(url, request)
      .pipe(
        map((results: any[]) => {
          return results.map((result: any) => new StoreRanking(result));
        }),
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  /**
   * New API for rank-by-store endpoint
   */
  findRankByStore(storeUuid: string, request: StoreRankingRequest): Observable<StoreRanking[]> {
    // Build URL: /api/insights/rank-by-store/<STORE_UUID>/ranking
    const url = this.buildUrl(`/rank-by-store/${storeUuid}/ranking`);
    // Convert request to JSON payload, excluding fips field
    const payload = request.toJSON();

    return this.http.post(url, payload)
      .pipe(
        map((results: any[]) => {
          return results.map((result: any) => new StoreRanking(result));
        }),
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  /**
   * Comprehensive ranking API for detailed ranking table
   * Endpoint: POST /api/insights/rank-by-store/comprehensive?page=1&size=10&sort=field,asc
   * @param request  - Comprehensive ranking request payload
   * @param page     - Page number (1-indexed — API returns "page":1 for first page)
   * @param size     - Number of items per page
   * @param sort     - Optional sort string e.g. "national_rank_banner,asc"
   * @returns Observable with ranking data
   */
  getComprehensiveRanking(request: any, page: number = 1, size: number = 10, sort?: string): Observable<any> {
    const url = this.buildUrl('/rank-by-store/comprehensive');
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (sort) {
      params = params.set('sort', sort);
    }

    return this.http.post(url, request, { params })
      .pipe(
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  /**
   * Drive miles/minutes ranking API — returns a plain array (no pagination).
   * Endpoint: POST /api/insights/rank-by-store/drive_miles/ranking
   */
  getDriveMilesRanking(request: any): Observable<any> {
    const url = this.buildUrl('/rank-by-store/drive_miles/ranking');

    return this.http.post(url, request)
      .pipe(
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  /**
   * Handles HTTP errors from the ranking API
   * @param error - The HTTP error response
   * @returns Observable that throws an error with a descriptive message
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred while fetching store rankings';

    if (error.status === 401) {
      errorMessage = 'Authentication failed. Please log in again.';
    } else if (error.status === 403) {
      errorMessage = 'You do not have permission to view store rankings.';
    } else if (error.status === 404) {
      errorMessage = 'Store not found.';
    } else if (error.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    }

    console.error('StoreRankingService error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
